import { useSnackbar } from "notistack";
import { FC, useEffect, useState } from "react";
import { Button, FormControl, MenuItem, Select, Stack, TextField } from "@mui/material";

import { api } from "../../../../../utils";
import { BranchModel } from "../../../../../models/AdminPageModels";
import { BoothClientModel, IError } from "../../../../../models/ReportPageModels";

interface AdminIccBranches {
    data: IccBranchesData;
    error: IError;
}
interface IccBranchesData {
    branches: BranchModel[];
}

export interface UsernamesAndBoothIdsResponse {
    data: UsernamesAndBoothIdsData;
    error: IError;
}
export interface UsernamesAndBoothIdsData {
    usernames: string[];
    boothIds: string[];
}

interface CreateIccUserRequest {
    username: string;
    password: string;
    branchId: number;
    boothId: string;
}
interface CreateIccUserResponse {
    data: CreateIccUserData;
    error: IError;
}
interface CreateIccUserData {
    status: string;
}

interface BoothIdResponseModel {
    data: BoothIdData;
    error: IError;
}
interface BoothIdData {
    boothIds: BoothClientModel[];
}

interface CreateIccUserProps {
    workflowNextStep?: () => void;
    createdBranchName?: string | null;
}

const CreateIccUser: FC<CreateIccUserProps> = ({ workflowNextStep = null, createdBranchName = null }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [username, setUsername] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [selectedBranch, setSelectedBranch] = useState<number>(0);
    const [boothId, setBoothId] = useState<string>("");

    const [branches, setBranches] = useState<BranchModel[]>([]);
    const [usernames, setUsernames] = useState<string[]>([]);
    const [boothIds, setBoothIds] = useState<string[]>([]);

    const [boothIdExamples, setBoothIdExamples] = useState<BoothClientModel[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        GetBranches();
        GetUsernamesAndBoothIds();
    }, []);

    const GetBranches = async () => {
        try {
            const response: AdminIccBranches = await api.get("api/Admin/GetICCBranches", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setBranches(response.data.branches);

                // branch created in workflow previous step
                if (createdBranchName) {
                    const branch = response.data.branches.find(b => b.branchName === createdBranchName);
                    if (branch) {
                        setSelectedBranch(branch.id);
                    }
                }
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const GetUsernamesAndBoothIds = async () => {
        try {
            const response: UsernamesAndBoothIdsResponse = await api.get("api/Admin/GetUsernamesAndBoothIds", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setIsLoading(false);

                setUsernames(response.data.usernames);
                setBoothIds(response.data.boothIds);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function CompanyChanged(event: any) {
        const value: number = event.target.value;
        setSelectedBranch(value);

        var companyName = "";
        if (value === 0) {
            setBoothIdExamples([]);
            return;
        }
        companyName = branches.find(b => b.id === value)?.branchName || "";
        GetCompanyBoothIds(companyName);
    }

    const GetCompanyBoothIds = async (companyName: string) => {
        try {
            const response: BoothIdResponseModel = await api.get(`api/Admin/GetBoothIdExamples?companyName=${companyName}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setBoothIdExamples(response.data.boothIds);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const CreateClient = async () => {
        if (username === "" || password === "" || selectedBranch === 0 || boothId === "") {
            enqueueSnackbar("Please recheck fields", { variant: "error" });
            return;
        }

        var body: CreateIccUserRequest = {
            username: username,
            password: password,
            branchId: selectedBranch,
            boothId: boothId
        }

        try {
            const response: CreateIccUserResponse = await api.post("api/Admin/CreateIccUser", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Created!", { variant: "success" })

                // for workflow function to step passed
                if (workflowNextStep)
                    workflowNextStep();

                // clear fields
                setUsername("");
                setPassword("");
                setSelectedBranch(0);
                setBoothId("");
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const IsUsernameValid = !usernames.includes(username);
    const IsBoothIdValid = !boothIds.includes(boothId);
    const isFormValid = IsUsernameValid && IsBoothIdValid && username !== "" && password !== "" && selectedBranch !== 0 && boothId !== "";

    return (
        <Stack
            direction="row"
            spacing={10}>
            {/* Fields */}
            <Stack>
                {/* Branch */}
                <Stack
                    direction={"row"}
                    alignItems={"center"}>
                    <p style={{ width: '110px' }}>BranchId: </p>
                    <FormControl sx={{ minWidth: 245 }}>
                        <Select
                            value={selectedBranch}
                            onChange={CompanyChanged}
                            size="small"
                            defaultValue={-1}>
                            <MenuItem
                                value={0}>
                                {"Select branch"}
                            </MenuItem>
                            {branches.map((type, index) =>
                                <MenuItem
                                    key={index}
                                    value={type.id}>
                                    {type.branchName}
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Stack>

                {/* Username */}
                <Stack
                    direction={"row"}
                    alignItems={"center"} >
                    <p style={{ width: '110px' }}>Username: </p>
                    <TextField
                        value={username}
                        onChange={(newValue: any) => { setUsername(newValue.target.value) }}
                        sx={{ minWidth: 245 }}
                        size="small"
                        error={!IsUsernameValid}
                        helperText={!IsUsernameValid ? "Username is already in use" : ""}
                        disabled={isLoading} />
                </Stack>

                {/* Password */}
                <Stack
                    direction={"row"}
                    alignItems={"center"}>
                    <p style={{ width: '110px' }}>Password: </p>
                    <TextField
                        value={password}
                        onChange={(newValue: any) => { setPassword(newValue.target.value) }}
                        sx={{ minWidth: 245 }}
                        size="small" />
                </Stack>

                {/* BoothId */}
                <Stack
                    direction={"row"}
                    alignItems={"center"}>
                    <p style={{ width: '110px' }}>BoothId: </p>
                    <TextField
                        value={boothId}
                        onChange={(newValue: any) => { setBoothId(newValue.target.value) }}
                        sx={{ minWidth: 245 }}
                        size="small"
                        error={!IsBoothIdValid}
                        helperText={!IsBoothIdValid ? "Booth ID is already in use" : ""}
                        disabled={isLoading} />
                </Stack>

                <Button
                    variant="outlined"
                    onClick={() => CreateClient()}
                    sx={{ width: '82px' }}
                    disabled={!isFormValid}>
                    Create
                </Button>
            </Stack>
            {/* BoothId examples */}
            <Stack>
                {boothIdExamples.map((boothId, index) => (
                    <span key={index}><b>{boothId.boothId}</b> {boothId.name}</span>
                ))}
            </Stack>
        </Stack>
    )
}

export default CreateIccUser;